import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, Languages } from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@vnetwork/datacentral-ui/primitives'
import TopbarAction from '@/components/TopbarAction'
import {
    SUPPORTED_LANGUAGES,
    persistLanguage,
    toSupportedLanguage,
    type LanguageCode,
} from '@/configs/i18n/languages'

/**
 * Topbar language switch: a TopbarAction (34px box, 16px glyph, tooltip,
 * aria-label) with the displayed language's code (VI / EN) as a small tag on
 * its corner, opening a menu of the supported languages, each named in itself.
 *
 * Choosing one calls `i18n.changeLanguage` (every namespace, packages
 * included, re-renders without reload; dayjs and the DS UIProvider locale
 * follow, see configs/i18n/languages.ts) and stores it in localStorage `lang`,
 * which i18n reads at startup.
 */
const LanguageSwitch = () => {
    const { t, i18n } = useTranslation()
    const current = toSupportedLanguage(i18n.resolvedLanguage ?? i18n.language)
    const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === current)!
    const currentLabel = currentLang.label

    const [menuOpen, setMenuOpen] = useState(false)
    const [afterClose, setAfterClose] = useState(false)

    const select = (code: LanguageCode) => {
        if (code === current) return
        persistLanguage(code)
        void i18n.changeLanguage(code)
    }

    const title = t('language-switch.title')

    return (
        <DropdownMenu
            onOpenChange={(open) => {
                // Closing hands focus back to the button: keep its tooltip shut
                // until the pointer or focus leaves.
                setMenuOpen(open)
                if (!open) setAfterClose(true)
            }}
        >
            <span className="relative inline-flex">
                <DropdownMenuTrigger asChild>
                    <TopbarAction
                        icon={Languages}
                        aria-label={`${title}: ${currentLabel}`}
                        tooltip={`${title}: ${currentLabel}`}
                        data-testid="language-switch-trigger"
                        suppressTooltip={menuOpen || afterClose}
                        onPointerLeave={() => setAfterClose(false)}
                        onBlur={() => setAfterClose(false)}
                    />
                </DropdownMenuTrigger>
                <span
                    aria-hidden="true"
                    data-testid="language-switch-code"
                    className="pointer-events-none absolute -bottom-0.5 -right-1 rounded-sm border border-border-subtle bg-surface px-0.5 text-[0.625rem] font-semibold leading-3 text-muted-foreground"
                >
                    {currentLang.short}
                </span>
            </span>
            <DropdownMenuContent
                align="end"
                className="w-44 max-w-[calc(100vw-1rem)]"
                data-testid="language-switch-menu"
            >
                {SUPPORTED_LANGUAGES.map(({ code, label }) => (
                    <DropdownMenuItem
                        key={code}
                        lang={code}
                        onSelect={() => select(code)}
                        data-testid={`language-option-${code}`}
                        aria-current={code === current ? 'true' : undefined}
                    >
                        <span className="min-w-0 flex-1">{label}</span>
                        {code === current ? (
                            <Check size={16} aria-hidden="true" />
                        ) : null}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default LanguageSwitch
